/**
 * deviceModelThumbnail — a picture of a device's OWN 3D model, for the cards
 * that used to draw only a glyph.
 *
 * The device 3D-model slot (`settings.model3d`, card t_6b2c5f01) is the model
 * the operator chose for a device — a parametric cistern family, or an IFC file
 * imported for it. In the Home 3D view that model is the device's presence. The
 * card faces had no way to show it: a tank read as a generic sensor glyph even
 * though its actual model was standing on the pin. The owner: *"we have
 * watertank with ifc model selected. display the model instead of icon"*.
 *
 * So this renders the model ONCE into a small transparent PNG and caches it by
 * the slot's own key. Cards then show an `<img>` — no live WebGL context per
 * tile (a Water page can hold a dozen tanks, and the 3D view already owns the
 * real canvas). A tank's picture follows the live level, quantised so a
 * percentage creeping up does not re-render every socket tick.
 *
 * Covers are deliberately NOT drawn here: a blind's model is all thin slats and
 * a window, which reads as mush at card size (the owner: *"ifc too big to show
 * on such small space.. we would need to bring back the svg"*). A cover's card
 * draws `components/devices/cover/CoverAnimation.vue` instead.
 *
 * The builders are the SHARED ones: `utils/bimTankRender.ts` paints the cistern
 * exactly as the house does, and an imported file goes through the one web-ifc
 * parser every uploaded IFC uses (`utils/bimIfcAsset.ts`). A family this module
 * cannot build answers `null` and the card keeps its illustration.
 */

import * as THREE from 'three';

import { parseBimIfcAsset, wasmBasePath } from './bimIfcAsset';
import { ralRgb, unitScaleForBounds, type DeviceModel3d } from './bimDeviceModel';
import { tankKindOf, tankMaterials, tankPartGeometry, tankPartsFor } from './bimTankRender';
import { WATER_TANK_DEFAULT_LEVEL } from './bimWaterTankParts';

/** The families this preview can build — the tanks (the ask that started it). */
function tankLibrary(model: DeviceModel3d): boolean {
  const type = model.library?.type;
  return type === 'water-tank' || type === 'waste-tank';
}

/** The level a tank is drawn at, quantised to 2 % (a 1 px step on a card). */
function previewLevel(levelPct: number | null | undefined): number {
  const value = levelPct === null || levelPct === undefined ? Number.NaN : Number(levelPct);
  if (!Number.isFinite(value)) return WATER_TANK_DEFAULT_LEVEL;
  const clamped = Math.min(100, Math.max(0, value));
  return Math.round(clamped / 2) * 2;
}

/**
 * The cache key for one model picture, or null when this module cannot draw the
 * slot. An IFC file's picture does not depend on the tank level (the file is the
 * model), so the level is only part of a library tank's key.
 */
export function deviceModelThumbnailKey(
  model: DeviceModel3d | null | undefined,
  levelPct: number | null | undefined,
): string | null {
  if (!model) return null;
  if (model.source === 'library') {
    if (!tankLibrary(model)) return null;
    const library = model.library;
    return `library|${library?.type}|${library?.model}|${JSON.stringify(library?.prefs ?? {})}|${previewLevel(levelPct)}`;
  }
  if (!model.ifc) return null;
  return `ifc|${model.ifc.url}|${model.ifc.scale ?? 1}|${model.placement.yawDeg ?? 0}`;
}

/** How many pictures one session keeps (a tank at every 2 % fits comfortably). */
const CACHE_LIMIT = 64;

const pictures = new Map<string, string>();
const pending = new Map<string, Promise<string | null>>();

let renderer: THREE.WebGLRenderer | null = null;
let rendererUnavailable = false;

/** The one offscreen renderer every picture is drawn with (one GL context). */
function offscreenRenderer(): THREE.WebGLRenderer | null {
  if (rendererUnavailable) return null;
  if (renderer) return renderer;
  try {
    const canvas = document.createElement('canvas');
    renderer = new THREE.WebGLRenderer({
      canvas,
      alpha: true,
      antialias: true,
      // The picture is read back after `render`, so the buffer must survive it.
      preserveDrawingBuffer: true,
    });
    renderer.setPixelRatio(1);
    renderer.setClearColor(0x000000, 0);
    return renderer;
  } catch {
    // No WebGL (an old device, a disabled GPU): the card keeps its illustration.
    rendererUnavailable = true;
    return null;
  }
}

function remember(key: string, picture: string): void {
  pictures.set(key, picture);
  while (pictures.size > CACHE_LIMIT) {
    const oldest = pictures.keys().next().value;
    if (oldest === undefined) break;
    pictures.delete(oldest);
  }
}

/** One drawn preview's object, with what the caller must release afterwards. */
interface PreviewSubject {
  object: THREE.Object3D;
  /** Geometry this build created (a prism) — shared primitives are not owned. */
  ownedGeometry: THREE.BufferGeometry[];
  /** Imported materials are per-file and released with it. */
  ownedMaterial: THREE.Material[];
}

/**
 * The cistern in the slot, built in the tank's own frame (X width, Y depth,
 * Z up) and then stood up the way the scene stands it.
 */
function tankSubject(model: DeviceModel3d, levelPct: number | null | undefined): PreviewSubject {
  const library = model.library;
  const materials = tankMaterials(ralRgb(library?.prefs.color), tankKindOf(library));
  const parts = tankPartsFor(library, previewLevel(levelPct));
  const frame = new THREE.Group();
  const ownedGeometry: THREE.BufferGeometry[] = [];
  for (const part of parts) {
    const { geometry, owned } = tankPartGeometry(part);
    if (owned) ownedGeometry.push(geometry);
    const mesh = new THREE.Mesh(geometry, materials[part.material]);
    mesh.position.set(part.center[0], part.center[1], part.center[2]);
    if (part.shape === 'prism') mesh.scale.set(1, 1, 1);
    else mesh.scale.set(part.size[0], part.size[1], part.size[2]);
    frame.add(mesh);
  }
  // The module's Z-up frame → three's Y-up world, then the slot's own turn.
  frame.rotation.x = -Math.PI / 2;
  const object = new THREE.Group();
  object.rotation.y = ((model.placement.yawDeg ?? 0) * Math.PI) / 180;
  object.add(frame);
  return { object, ownedGeometry, ownedMaterial: [] };
}

/** The file the operator imported, stood up in its own frame (Z-up → Y-up). */
async function ifcSubject(model: DeviceModel3d): Promise<PreviewSubject | null> {
  const ifc = model.ifc;
  if (!ifc) return null;
  const bytes = await fetchIfcBytes(ifc.url);
  if (!bytes) return null;
  const parsed = await parseBimIfcAsset(bytes, { wasmPath: wasmBasePath(), castShadow: 'none' });
  if (parsed.meshes.length === 0) return null;
  const unit = (ifc.scale ?? 1) * unitScaleForBounds(parsed.size ?? [1, 1, 1]);
  const centre = parsed.center ?? [0, 0, 0];
  const frame = new THREE.Group();
  frame.rotation.x = -Math.PI / 2;
  const offset = new THREE.Group();
  offset.position.set(-centre[0] * unit, -centre[1] * unit, -centre[2] * unit);
  offset.scale.setScalar(unit);
  for (const mesh of parsed.meshes) {
    const clone = new THREE.Mesh(mesh.geometry, mesh.material);
    clone.position.copy(mesh.position);
    clone.quaternion.copy(mesh.quaternion);
    clone.scale.copy(mesh.scale);
    offset.add(clone);
  }
  frame.add(offset);
  const object = new THREE.Group();
  object.rotation.y = ((model.placement.yawDeg ?? 0) * Math.PI) / 180;
  object.add(frame);
  return {
    object,
    ownedGeometry: parsed.meshes.map((mesh) => mesh.geometry),
    ownedMaterial: parsed.meshes.flatMap((mesh) => (Array.isArray(mesh.material) ? mesh.material : [mesh.material])),
  };
}

/**
 * The imported file's bytes, over the app's own transport — the same door the
 * 3D layer reads it through (`routedFetch`, with the hub token), so a model
 * imported on a hub-served page, in Electron or through the cloud's WebRTC
 * channel resolves here too.
 */
async function fetchIfcBytes(url: string): Promise<Uint8Array | null> {
  try {
    const [{ routedFetch }, { useAuthStore }] = await Promise.all([
      import('@/services/apiRouter'),
      import('@/stores/auth'),
    ]);
    const auth = useAuthStore();
    const headers: Record<string, string> = {};
    if (auth.token) headers.Authorization = `Bearer ${auth.token}`;
    const response = await routedFetch(url, { headers });
    if (!response.ok) return null;
    return new Uint8Array(await response.arrayBuffer());
  } catch {
    return null;
  }
}

/** Draw one subject into a square transparent picture. */
function draw(subject: PreviewSubject, sizePx: number): string | null {
  const gl = offscreenRenderer();
  if (!gl) return null;
  const scene = new THREE.Scene();
  scene.add(new THREE.HemisphereLight(0xffffff, 0x46566b, 1.5));
  scene.add(new THREE.AmbientLight(0xffffff, 0.55));
  const key = new THREE.DirectionalLight(0xffffff, 2.1);
  key.position.set(3, 6, 4);
  scene.add(key);
  const fill = new THREE.DirectionalLight(0x9fc4ff, 0.9);
  fill.position.set(-4, 2, -4);
  scene.add(fill);

  const box = new THREE.Box3().setFromObject(subject.object);
  if (box.isEmpty()) return null;
  const sphere = box.getBoundingSphere(new THREE.Sphere());
  const camera = new THREE.PerspectiveCamera(34, 1, 0.001, 1000);
  // A three-quarter view, a little above the model — how the pin frames it.
  const azimuth = (35 * Math.PI) / 180;
  const elevation = (22 * Math.PI) / 180;
  const direction = new THREE.Vector3(
    Math.cos(elevation) * Math.sin(azimuth),
    Math.sin(elevation),
    Math.cos(elevation) * Math.cos(azimuth),
  );
  const distance = (sphere.radius / Math.sin(((camera.fov / 2) * Math.PI) / 180)) * 1.12;
  camera.position.copy(sphere.center).addScaledVector(direction, distance);
  camera.lookAt(sphere.center);

  scene.add(subject.object);
  gl.setSize(sizePx, sizePx, false);
  gl.setClearColor(0x000000, 0);
  gl.render(scene, camera);
  scene.remove(subject.object);
  try {
    return gl.domElement.toDataURL('image/png');
  } catch {
    // A tainted canvas is not a picture the card can show.
    return null;
  }
}

/** Forget every cached picture (the rigs' door; nothing in the app calls it). */
export function clearDeviceModelThumbnails(): void {
  pictures.clear();
}

/**
 * The picture of one device's own model, or null when this slot has nothing the
 * cards can draw (no model, an unbuildable family, no WebGL, an unreadable
 * file). Cached by `deviceModelThumbnailKey`, with concurrent asks sharing one
 * render.
 */
export async function deviceModelThumbnail(
  model: DeviceModel3d | null | undefined,
  levelPct: number | null | undefined = null,
  sizePx = 160,
): Promise<string | null> {
  const key = deviceModelThumbnailKey(model, levelPct);
  if (!key || !model) return null;
  const cached = pictures.get(key);
  if (cached) return cached;
  const inFlight = pending.get(key);
  if (inFlight) return inFlight;

  const job = (async (): Promise<string | null> => {
    let subject: PreviewSubject | null = null;
    try {
      subject = model.source === 'library' ? tankSubject(model, levelPct) : await ifcSubject(model);
      if (!subject) return null;
      const picture = draw(subject, sizePx);
      if (picture) remember(key, picture);
      return picture;
    } catch {
      // A file the parser rejects, a shader that will not compile: the card
      // keeps its illustration, it never gets a broken mount.
      return null;
    } finally {
      if (subject) {
        for (const geometry of subject.ownedGeometry) geometry.dispose();
        for (const material of subject.ownedMaterial) material.dispose();
      }
    }
  })();

  pending.set(key, job);
  try {
    return await job;
  } finally {
    pending.delete(key);
  }
}
